import React, { useState } from 'react';
import { ArrowLeft, Filter, ArrowUpDown } from 'lucide-react';
import Header from '@/components/common/Header';
import CallCard from '@/components/common/CallCard';
import CallScreen from '@/screens/calls/CallScreen';
import { Call } from '@/types';

interface AllCallsScreenProps {
  calls: Call[];
  onBack: () => void;
  onCallClick: (callId: string) => void;
  onStatusChange: (callId: string, newStatus: any) => void;
  onNavigateToPayment?: (dispatchId: string) => void;
  onImpoundCreated?: (impoundId: string) => void;
  showHeader?: boolean;
}

const AllCallsScreen: React.FC<AllCallsScreenProps> = ({
  calls,
  onBack,
  onCallClick,
  onStatusChange,
  onNavigateToPayment,
  onImpoundCreated,
  showHeader = true,
}) => {
  const [filterOption, setFilterOption] = useState<'All' | 'Calls'>('Calls');
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);

  const handleCallCardClick = (callId: string) => {
    const call = calls.find((c: Call) => c.id === callId);
    if (call) {
      setSelectedCall(call);
    }
  };

  const handleBackFromCall = () => {
    setSelectedCall(null);
  };

  // If a call is selected, show the CallScreen instead
  if (selectedCall) {
    return (
      <CallScreen 
        callId={selectedCall.id} 
        onBack={handleBackFromCall}
        onNavigateToPayment={onNavigateToPayment}
        onImpoundCreated={onImpoundCreated}
        showHeader={showHeader}
      />
    );
  }

  // "Calls" = operational list (hide canceled). "All" includes canceled for record-keeping.
  const displayedCalls =
    filterOption === 'Calls'
      ? calls.filter((c) => c.status !== 'canceled')
      : calls;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-background">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={24} className="text-gray-800" />
          </button>
          <h1 className="text-2xl font-semibold text-black">All Calls</h1>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {/* Filter Bar */}
        <div className="mb-4 flex items-center justify-between">
          <div className="text-gray-600 text-sm">
            {displayedCalls.length} {displayedCalls.length === 1 ? 'call' : 'calls'}
          </div>
          <button
            onClick={() => setShowFilterPopup(!showFilterPopup)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
          >
            <Filter size={20} className="text-gray-600" />
            {showFilterPopup && (
              <div className="absolute right-0 top-full mt-1 bg-background border border-gray-200 rounded-xl shadow-xl z-50 min-w-[120px]">
                {(['All', 'Calls'] as const).map((option) => (
                  <button
                    key={option}
                    onClick={() => {
                      setFilterOption(option);
                      setShowFilterPopup(false);
                    }}
                    className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors first:rounded-t-xl last:rounded-b-xl ${
                      filterOption === option
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-gray-700'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </button>
        </div>

        {/* Calls List */}
        {displayedCalls.length > 0 ? (
          <div className="space-y-3 pb-20">
            {displayedCalls.map((call) => (
              <CallCard
                key={call.id}
                call={call}
                onStatusChange={onStatusChange}
                onCardClick={handleCallCardClick}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <p>No calls available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllCallsScreen;
